/**
 * Signature Integrate topics.
 *
 * These sit alongside the original Wellbeing Hub destinations (aromatherapy,
 * nature connection, etc.) but carry the Recovery Trauma™ point of view:
 * integrating recovery into ordinary daily life, drawing on DBT, IFS and
 * shadow work without ever presenting itself as therapy.
 *
 * Each topic renders through <IntegrateTopicPage />, so the cards, icons,
 * typography, spacing and colours match the existing section exactly.
 */

export type IntegrateCard = {
  id: string;
  emoji: string;
  title: string;
  description: string;
  ideas: string[];
  tint: string;
};

export type IntegrateTopic = {
  slug: string;
  emoji: string;
  /** Short label used on the hub card and the page eyebrow. */
  title: string;
  /** One-line hub card description. */
  description: string;
  /** Large page heading. */
  heroTitle: string;
  /** Supporting paragraph under the heading. */
  intro: string;
  /** e.g. "Inspired by DBT" — omitted where the topic is our own. */
  inspiredBy?: string;
  /**
   * Optional extra safety wording shown under the standard note. Used where a
   * topic can open something tender (e.g. unsent letters) and the member needs
   * the boundaries spelled out before they begin.
   */
  safetyNote?: string[];

  /** Heading above the bullet list inside each card. */
  ideasLabel: string;
  tint: string;
  cards: IntegrateCard[];
  reflection: string[];
};

export const INTEGRATE_TOPICS: IntegrateTopic[] = [
  {
    slug: "life-worth-living",
    emoji: "🌅",
    title: "Building a Life Worth Living",
    description: "Small, realistic steps towards a life that feels like yours.",
    heroTitle: "Build a life worth living",
    intro:
      "Surviving kept you here. This is about what comes next — values, small joys and ordinary days that feel worth waking up for.",
    inspiredBy: "Inspired by DBT",
    ideasLabel: "Invitations",
    tint: "#E8C07A",
    cards: [
      {
        id: "values",
        emoji: "🧭",
        title: "Personal Values",
        description:
          "What matters to you, underneath what you were told should matter.",
        ideas: [
          "Name three values you would keep even if no one noticed",
          "Notice a moment this week that felt true to you",
          "Write down one value you have been quietly betraying",
          "Choose one small action that honours a single value today",
        ],
        tint: "#E8C07A",
      },
      {
        id: "goals",
        emoji: "🌱",
        title: "Meaningful Goals",
        description: "Goals shaped around your life, not around recovery targets.",
        ideas: [
          "Pick one goal small enough to start this week",
          "Break it into the smallest first step you can imagine",
          "Ask what this goal would give you, beyond finishing it",
          "Let one old goal go if it no longer belongs to you",
        ],
        tint: "#C9A66B",
      },
      {
        id: "joys",
        emoji: "☕",
        title: "Daily Joys",
        description: "Tiny pleasures that ask nothing of you.",
        ideas: [
          "List five things that reliably feel good and cost nothing",
          "Plan one of them into tomorrow on purpose",
          "Notice the first pleasant moment of the day",
          "Keep a running note of small joys as you spot them",
        ],
        tint: "#E8A87C",
      },
      {
        id: "routine",
        emoji: "🕰️",
        title: "A Routine That Holds You",
        description:
          "Rhythm that supports you rather than another set of rules.",
        ideas: [
          "Anchor the day with one thing that always happens",
          "Add one nourishing moment to a routine you already have",
          "Notice which part of the day is hardest, and soften it",
          "Leave deliberate empty space in the week",
        ],
        tint: "#B7C4E0",
      },
      {
        id: "beyond",
        emoji: "🕊️",
        title: "Living Beyond Survival Mode",
        description:
          "Choosing more than getting through — slowly, and on your terms.",
        ideas: [
          "Notice one thing you do now that survival never allowed",
          "Say yes to something purely because you want it",
          "Ask what you would do this month if fear was quieter",
          "Mark one ordinary day as good, out loud",
        ],
        tint: "#7BAE7F",
      },
    ],
    reflection: [
      "What made today feel worth living, even slightly?",
      "Which value did you honour without noticing?",
      "What is one small thing you would like tomorrow to hold?",
    ],
  },
  {
    slug: "self-leadership",
    emoji: "🧭",
    title: "Self-Leadership",
    description: "Grow the calm, steady part of you that can lead.",
    heroTitle: "Lead yourself with kindness",
    intro:
      "There is a part of you that stays calm and curious even when everything else is loud. This is about giving that part the wheel more often.",
    inspiredBy: "Inspired by Internal Family Systems",
    ideasLabel: "Practices",
    tint: "#B7C4E0",
    cards: [
      {
        id: "respond",
        emoji: "⏸️",
        title: "Respond, Don't React",
        description: "Creating a small gap between the spark and the answer.",
        ideas: [
          "Take three breaths before replying to something charged",
          "Name the feeling before naming the problem",
          "Ask: does this need answering now, or later?",
          "Notice one moment you paused and let it count",
        ],
        tint: "#B7C4E0",
      },
      {
        id: "awareness",
        emoji: "🔍",
        title: "Self-Awareness",
        description: "Noticing what is happening in you, without commentary.",
        ideas: [
          "Check in three times today with one word each",
          "Notice where the feeling sits in your body",
          "Track what usually comes before a difficult mood",
          "Write what you noticed without deciding what it means",
        ],
        tint: "#9DAF60",
      },
      {
        id: "compassion",
        emoji: "🤍",
        title: "Self-Compassion",
        description: "Meeting yourself the way you would meet a friend.",
        ideas: [
          "Say the kinder sentence out loud, even if you doubt it",
          "Ask what you would say to someone you love in this moment",
          "Let one mistake be human rather than proof of anything",
          "Put a hand where the feeling is and stay a moment",
        ],
        tint: "#E8B4C8",
      },
      {
        id: "decisions",
        emoji: "⚖️",
        title: "Balanced Decisions",
        description: "Letting feeling and reason both have a seat.",
        ideas: [
          "Write the decision, then what fear says, then what care says",
          "Sleep on anything that feels urgent but not time-bound",
          "Ask which choice you could explain kindly in a year",
          "Choose the smallest reversible version first",
        ],
        tint: "#C9A66B",
      },
      {
        id: "trust",
        emoji: "🪢",
        title: "Building Self-Trust",
        description: "Trust rebuilt through small promises kept.",
        ideas: [
          "Make one promise to yourself small enough to keep",
          "Keep it, then notice that you did",
          "Renegotiate rather than abandon when it is too much",
          "Track kept promises for a week",
        ],
        tint: "#7BAE7F",
      },
    ],
    reflection: [
      "When did you lead yourself well today?",
      "Which part of you needed reassurance rather than instruction?",
      "What is one promise you can keep tomorrow?",
    ],
  },
  {
    slug: "your-inner-team",
    emoji: "👥",
    title: "Your Inner Team",
    description: "Different parts of you, each with something to say.",
    heroTitle: "Meet your inner team",
    intro:
      "You are not one single voice. Different parts of you carry different jobs, fears and hopes — and most of them are trying to help.",
    inspiredBy: "Inspired by Internal Family Systems",
    ideasLabel: "Explorations",
    tint: "#D58FB4",
    cards: [
      {
        id: "voices",
        emoji: "🎭",
        title: "Recognising Inner Voices",
        description: "Beginning to tell the different voices apart.",
        ideas: [
          "Notice the voice that criticises — what tone does it use?",
          "Notice the voice that protects, and what it protects from",
          "Give one part a plain, non-judgemental name",
          "Ask how old a part feels when it speaks",
        ],
        tint: "#D58FB4",
      },
      {
        id: "conflict",
        emoji: "🔀",
        title: "Internal Conflicts",
        description: "When part of you wants one thing and part wants another.",
        ideas: [
          "Write both sides of the argument in full",
          "Ask what each side is afraid would happen",
          "Look for the shared want underneath the disagreement",
          "Let the decision wait until both have been heard",
        ],
        tint: "#B7C4E0",
      },
      {
        id: "exercises",
        emoji: "🪑",
        title: "Reflective Exercises",
        description: "Gentle ways to sit with a part rather than silence it.",
        ideas: [
          "Picture the part sitting nearby and simply notice it",
          "Ask what it needs from you, then wait",
          "Thank a protective part for the job it has been doing",
          "Stop before anything feels overwhelming",
        ],
        tint: "#E8C07A",
      },
      {
        id: "prompts",
        emoji: "✍️",
        title: "Journalling Prompts",
        description: "Writing as a way to let each part speak in turn.",
        ideas: [
          "Which part showed up most this week?",
          "What would the anxious part say if it trusted me?",
          "What does the part that pushes hardest want for me?",
          "Which part have I been ignoring?",
        ],
        tint: "#9DAF60",
      },
      {
        id: "with",
        emoji: "🤝",
        title: "With, Not Against",
        description: "Working alongside yourself instead of fighting yourself.",
        ideas: [
          "Replace 'why am I like this' with 'what is this part doing for me'",
          "Negotiate rather than override",
          "Notice when a part relaxes because it felt heard",
          "Let disagreement inside you be allowed",
        ],
        tint: "#7BAE7F",
      },
    ],
    reflection: [
      "Which part of you spoke loudest today?",
      "What was it trying to protect?",
      "What would it need in order to soften?",
    ],
  },
  {
    slug: "triggers-as-teachers",
    emoji: "🔥",
    title: "Triggers as Teachers",
    description: "Meeting your reactions with curiosity, not judgement.",
    heroTitle: "Triggers as teachers",
    intro:
      "A trigger is information, not failure. Approached gently and only when you have capacity, it can show you what still needs care.",
    inspiredBy: "Inspired by shadow work",
    ideasLabel: "Reflections",
    tint: "#E8A87C",
    cards: [
      {
        id: "identify",
        emoji: "📌",
        title: "Identifying Triggers",
        description: "Noticing what reliably moves you, and when.",
        ideas: [
          "Note the moment your body changed before your thoughts did",
          "Record time, place and who was there",
          "Look for the sentence that landed hardest",
          "Keep the list descriptive, not critical",
        ],
        tint: "#E8A87C",
      },
      {
        id: "reflect",
        emoji: "🪞",
        title: "Reflection Exercises",
        description: "Looking back once the wave has passed, not during it.",
        ideas: [
          "Wait until you feel steady before reflecting",
          "Ask what the reaction was defending",
          "Notice how old the feeling felt",
          "Write it as though describing a friend's day",
        ],
        tint: "#D58FB4",
      },
      {
        id: "understand",
        emoji: "🌊",
        title: "Understanding Reactions",
        description: "Your response made sense somewhere, once.",
        ideas: [
          "Ask when this reaction would have kept you safe",
          "Separate the present situation from the old one",
          "Name what is actually happening now, plainly",
          "Let the reaction be understandable without being the plan",
        ],
        tint: "#B7C4E0",
      },
      {
        id: "patterns",
        emoji: "🔁",
        title: "Recurring Patterns",
        description: "The same door, opening again and again.",
        ideas: [
          "Look for the theme across three recent triggers",
          "Notice which relationships repeat the pattern",
          "Ask what changes just before it happens",
          "Choose one small point where you could step differently",
        ],
        tint: "#C9A66B",
      },
      {
        id: "growth",
        emoji: "🌿",
        title: "Growth From Here",
        description: "Turning what you learned into something small and doable.",
        ideas: [
          "Decide one boundary this taught you",
          "Plan what you will do in the first sixty seconds next time",
          "Tell someone safe what you noticed",
          "Mark the times you handled it differently",
        ],
        tint: "#7BAE7F",
      },
    ],
    reflection: [
      "What did today's reaction want you to know?",
      "How old did the feeling seem?",
      "What is one gentler response you could try next time?",
    ],
  },
  {
    slug: "hidden-strengths",
    emoji: "💎",
    title: "Hidden Strengths",
    description: "The capacities you built without ever calling them strengths.",
    heroTitle: "Your hidden strengths",
    intro:
      "You did not come through what you came through with nothing. Some of what you learned is genuinely yours to keep.",
    inspiredBy: "Inspired by shadow work",
    ideasLabel: "Ways to notice it",
    tint: "#9DAF60",
    cards: [
      {
        id: "resilience",
        emoji: "🪨",
        title: "Resilience",
        description: "The quiet capacity to keep going and to come back.",
        ideas: [
          "List three things you have already survived",
          "Notice what helped you return, not just endure",
          "Name one hard week you got through this year",
          "Let resilience include resting",
        ],
        tint: "#9DAF60",
      },
      {
        id: "empathy",
        emoji: "🫂",
        title: "Empathy",
        description: "Reading rooms and people, often before they speak.",
        ideas: [
          "Notice when you sensed something others missed",
          "Ask where empathy serves you and where it costs you",
          "Offer yourself the empathy you hand out freely",
          "Practise noticing without absorbing",
        ],
        tint: "#E8B4C8",
      },
      {
        id: "creativity",
        emoji: "🎨",
        title: "Creativity",
        description: "Imagination that once made hard places survivable.",
        ideas: [
          "Recall how you escaped or reframed as a child",
          "Make something small with no purpose at all",
          "Notice where you improvise well",
          "Give creativity fifteen unjudged minutes",
        ],
        tint: "#E8A87C",
      },
      {
        id: "courage",
        emoji: "🦁",
        title: "Courage",
        description: "Doing it while afraid, which is the only kind there is.",
        ideas: [
          "Name a time you acted despite fear",
          "Notice the courage in asking for help",
          "Count saying no as bravery",
          "Choose one small brave thing this week",
        ],
        tint: "#C9A66B",
      },
      {
        id: "adaptability",
        emoji: "🌬️",
        title: "Adaptability & Problem Solving",
        description: "Finding a way through when the usual route closed.",
        ideas: [
          "List three problems you quietly solved recently",
          "Notice how quickly you adjust when plans change",
          "Ask where this skill is useful now, not just then",
          "Let 'good enough' be a real solution",
        ],
        tint: "#B7C4E0",
      },
    ],
    reflection: [
      "Which strength did you use today without crediting yourself?",
      "Where did it come from?",
      "How could you use it kindly this week?",
    ],
  },
  {
    slug: "letters-never-sent",
    emoji: "✉️",
    title: "Letters You'll Never Send",
    description: "Write it all out — then keep it or delete it, your choice.",
    heroTitle: "Letters you'll never send",
    intro:
      "Some things need saying without being received. Write freely, take breaks, and keep or delete whatever you write — nothing here has to be sent.",
    safetyNote: [
      "This letter never has to be sent, read by anyone, or finished.",
      "You can stop at any point — a few lines is enough.",
      "You do not need to write every detail. Leave out anything you are not ready to put into words.",
      "Afterwards, do something steadying: a drink of water, fresh air, a few slow breaths.",
      "Anything you write in the Journal is private to you. You can save it, edit it, or delete it whenever you want.",
    ],
    ideasLabel: "Prompts to begin",

    tint: "#D58FB4",
    cards: [
      {
        id: "younger",
        emoji: "🧸",
        title: "To Your Younger Self",
        description: "The words nobody said to you at the time.",
        ideas: [
          "Tell them what was not their fault",
          "Describe what you wish someone had noticed",
          "Tell them how the story continues",
          "End with one sentence of comfort",
        ],
        tint: "#E8B4C8",
      },
      {
        id: "future",
        emoji: "🌤️",
        title: "To Your Future Self",
        description: "A note forward, for a day you cannot see yet.",
        ideas: [
          "Say what you are carrying right now",
          "Ask them one honest question",
          "Tell them what you hope has softened",
          "Write the date you would like to read it",
        ],
        tint: "#E8C07A",
      },
      {
        id: "hurt",
        emoji: "🗯️",
        title: "To Someone Who Hurt You",
        description:
          "Unsent, unedited, unread by them. Stop whenever you need to.",
        ideas: [
          "Write it exactly as it comes, without softening",
          "Say the thing you were never allowed to say",
          "Name what it cost you",
          "Close the notebook and do something steadying afterwards",
        ],
        tint: "#E8A87C",
      },
      {
        id: "miss",
        emoji: "🕯️",
        title: "To Someone You Miss",
        description: "For grief, distance or a door that closed.",
        ideas: [
          "Tell them what you never got to finish saying",
          "Describe what you carry of them",
          "Say what you would ask if you could",
          "Let it be unfinished if it needs to be",
        ],
        tint: "#B7C4E0",
      },
      {
        id: "self",
        emoji: "💛",
        title: "To Yourself",
        description: "The letter you would write to someone you love.",
        ideas: [
          "Begin with 'I know this has been hard'",
          "Write only what you would say to a friend",
          "Forgive one thing on paper",
          "Sign it with your own name",
        ],
        tint: "#D58FB4",
      },
    ],
    reflection: [
      "What felt lighter once it was on the page?",
      "Do you want to keep this letter, or let it go?",
      "What do you need in the next hour to feel steady?",
    ],
  },
  {
    slug: "comfort-collections",
    emoji: "🧺",
    title: "Comfort Collections",
    description: "Gather the things that reliably soothe you, in one place.",
    heroTitle: "Build your comfort collection",
    intro:
      "On hard days, nobody can remember what helps. Gather it now, while you can, so the list is waiting for you later.",
    ideasLabel: "What to gather",
    tint: "#E8B4C8",
    cards: [
      {
        id: "sound",
        emoji: "🎵",
        title: "Songs & Sounds",
        description: "The audio that reaches you when words cannot.",
        ideas: [
          "Make a playlist for the hardest hour of the day",
          "Add one song that always steadies you",
          "Include a sound with no words at all",
          "Note which songs to avoid on low days",
        ],
        tint: "#B7C4E0",
      },
      {
        id: "words",
        emoji: "📜",
        title: "Words & Quotes",
        description: "Sentences worth keeping close.",
        ideas: [
          "Collect three lines that help you breathe",
          "Save a message someone kind sent you",
          "Write one sentence in your own words",
          "Keep them where you can reach them quickly",
        ],
        tint: "#E8C07A",
      },
      {
        id: "stories",
        emoji: "📚",
        title: "Books & Films",
        description: "Familiar stories that ask nothing new of you.",
        ideas: [
          "List the comfort re-watch you can quote",
          "Keep one gentle book by the bed",
          "Note what to reach for when concentration is low",
          "Allow the same one, again",
        ],
        tint: "#C9A66B",
      },
      {
        id: "senses",
        emoji: "🕯️",
        title: "Scents, Tastes & Textures",
        description: "Comfort that arrives through the body first.",
        ideas: [
          "Choose one scent that means safety",
          "Keep a blanket that feels right to touch",
          "Note the food that is easy on a bad day",
          "Add a warm drink you actually like",
        ],
        tint: "#E8A87C",
      },
      {
        id: "places",
        emoji: "🖼️",
        title: "Photos, Places & People",
        description: "Reminders of where and with whom you feel held.",
        ideas: [
          "Save five photographs that soften you",
          "Name one place you can go and simply sit",
          "List two people who are safe to message",
          "Add one reminder in your own handwriting",
        ],
        tint: "#7BAE7F",
      },
    ],
    reflection: [
      "What comforted you today, however small?",
      "What is missing from your collection?",
      "Where will you keep it so it is easy to find?",
    ],
  },
  {
    slug: "finding-awe",
    emoji: "✨",
    title: "Finding Awe",
    description: "Noticing wonder in an ordinary week.",
    heroTitle: "Finding awe",
    intro:
      "Awe widens the world when trauma has narrowed it. It rarely needs anything dramatic — mostly it needs noticing.",
    ideasLabel: "Where to look",
    tint: "#B7C4E0",
    cards: [
      {
        id: "sky",
        emoji: "🌇",
        title: "Sky & Weather",
        description: "The free spectacle happening above you daily.",
        ideas: [
          "Watch one sunset from beginning to end",
          "Look up at the clouds for sixty seconds",
          "Step outside after dark and find one star",
          "Notice how the light changes through the day",
        ],
        tint: "#E8C07A",
      },
      {
        id: "wild",
        emoji: "🦋",
        title: "Nature & Wildlife",
        description: "Small living things, entirely uninterested in you.",
        ideas: [
          "Follow one bird until it leaves",
          "Find something growing through concrete",
          "Sit near water for five minutes",
          "Notice the nearest tree properly",
        ],
        tint: "#7BAE7F",
      },
      {
        id: "made",
        emoji: "🎼",
        title: "Art, Music & Architecture",
        description: "What other people made, and left behind.",
        ideas: [
          "Listen to one piece of music with your eyes closed",
          "Look up at a building you walk past daily",
          "Visit one gallery room, then leave",
          "Find a piece of art that unsettles you pleasantly",
        ],
        tint: "#D58FB4",
      },
      {
        id: "learn",
        emoji: "🔭",
        title: "Learning Something New",
        description: "Wonder that arrives through understanding.",
        ideas: [
          "Read one fact about space, oceans or bodies",
          "Learn what a nearby plant is called",
          "Watch a short film about something you know nothing about",
          "Ask a question you have never asked",
        ],
        tint: "#B7C4E0",
      },
      {
        id: "capture",
        emoji: "📷",
        title: "Noticing & Capturing",
        description: "Keeping the moment without needing to perform it.",
        ideas: [
          "Photograph one thing a day for a week",
          "Write down the moment instead of photographing it",
          "Keep an 'awe list' in your notes",
          "Share one with someone who would enjoy it",
        ],
        tint: "#C9A66B",
      },
    ],
    reflection: [
      "What made you pause today?",
      "Where in your body did the wonder land?",
      "What would you like to look for tomorrow?",
    ],
  },
  {
    slug: "the-recovery-home",
    emoji: "🏡",
    title: "The Recovery Home",
    description: "Shaping the space around you so it supports your nervous system.",
    heroTitle: "A home that holds you",
    intro:
      "Your surroundings speak to your nervous system all day. Small changes to the space you live in can make regulation easier.",
    ideasLabel: "Small changes",
    tint: "#C9A66B",
    cards: [
      {
        id: "light",
        emoji: "💡",
        title: "Light",
        description: "The fastest change to how a room feels.",
        ideas: [
          "Swap the overhead light for a lamp in the evening",
          "Let daylight in first thing where you can",
          "Use warmer bulbs in the room you rest in",
          "Notice which lighting makes you tense",
        ],
        tint: "#E8C07A",
      },
      {
        id: "corner",
        emoji: "🛋️",
        title: "A Corner That Is Yours",
        description: "One small place set aside for rest.",
        ideas: [
          "Clear one chair or corner and keep it clear",
          "Add a blanket that feels good",
          "Keep a book and a glass of water there",
          "Use it for nothing productive",
        ],
        tint: "#B7C4E0",
      },
      {
        id: "green",
        emoji: "🪴",
        title: "Plants & Living Things",
        description: "Something growing in the room with you.",
        ideas: [
          "Start with one plant that is hard to kill",
          "Put it where you will actually see it",
          "Let caring for it be small and forgiving",
          "Add cut greenery if plants feel like pressure",
        ],
        tint: "#7BAE7F",
      },
      {
        id: "scent",
        emoji: "🕯️",
        title: "Scent & Sound",
        description: "The atmosphere you notice without looking.",
        ideas: [
          "Choose one calming scent for evenings",
          "Keep background sound low and predictable",
          "Notice which noises put you on alert",
          "Try silence and see how it sits",
        ],
        tint: "#D58FB4",
      },
      {
        id: "order",
        emoji: "🧹",
        title: "Gentle Order",
        description: "Enough tidiness to breathe — not perfection.",
        ideas: [
          "Clear one surface, not one room",
          "Set a ten-minute timer and stop when it ends",
          "Give the things you use daily a home",
          "Let 'good enough' be finished",
        ],
        tint: "#C9A66B",
      },
    ],
    reflection: [
      "Which room feels hardest to be in, and why?",
      "What single change would help most?",
      "Where in your home do you feel most yourself?",
    ],
  },
  {
    slug: "micro-adventures",
    emoji: "🧭",
    title: "Micro Adventures",
    description: "Small novelty, no planning, back home by evening.",
    heroTitle: "Micro adventures",
    intro:
      "Novelty helps a narrowed world open again — and it does not need a passport, a plan or a whole day.",
    ideasLabel: "Ideas to try",
    tint: "#7BAE7F",
    cards: [
      {
        id: "route",
        emoji: "🚶",
        title: "A Different Route",
        description: "The same journey, taken a new way.",
        ideas: [
          "Walk home a street you have never used",
          "Get off one stop early",
          "Turn left where you normally turn right",
          "Notice three things you have never seen before",
        ],
        tint: "#9DAF60",
      },
      {
        id: "nearby",
        emoji: "🗺️",
        title: "Somewhere Nearby",
        description: "The places within reach you keep meaning to visit.",
        ideas: [
          "Visit a café you have walked past for years",
          "Find the nearest green space you have not been to",
          "Pick a spot on the map within an hour and go",
          "Go alone if that feels easier",
        ],
        tint: "#B7C4E0",
      },
      {
        id: "early",
        emoji: "🌄",
        title: "Different Hours",
        description: "Familiar places feel new at unfamiliar times.",
        ideas: [
          "Watch one sunrise",
          "Walk somewhere quiet late in the evening",
          "See your street before the traffic starts",
          "Take a break outdoors mid-morning",
        ],
        tint: "#E8C07A",
      },
      {
        id: "culture",
        emoji: "🏛️",
        title: "Museums & Local History",
        description: "Curiosity as an outing.",
        ideas: [
          "Visit one museum room and leave when you are done",
          "Look up who lived on your street a century ago",
          "Find the oldest building near you",
          "Read one plaque properly",
        ],
        tint: "#C9A66B",
      },
      {
        id: "daytrip",
        emoji: "🚆",
        title: "Day Trips & Nature Walks",
        description: "A little further, still home by night.",
        ideas: [
          "Take a train to the end of the line",
          "Walk somewhere with water",
          "Pack something nice to eat and stop halfway",
          "Let the plan stay loose",
        ],
        tint: "#7BAE7F",
      },
    ],
    reflection: [
      "What did you notice that you would have walked past?",
      "How did your body feel afterwards?",
      "Where would you like to go next?",
    ],
  },
];

export function getIntegrateTopic(slug: string): IntegrateTopic | undefined {
  return INTEGRATE_TOPICS.find((t) => t.slug === slug);
}
